import { takeLatest, put, call } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getDocumentConfigurationRequest,
  getDocumentConfigurationSuccess,
  getDocumentConfigurationFailure,
  uploadJewellerDocumentRequest,
  uploadJewellerDocumentSuccess,
  uploadJewellerDocumentFailure,
  saveDocumentConfigurationRequest,
  saveDocumentConfigurationSuccess,
  saveDocumentConfigurationFailure,
  getDocumentsRequest,
  getDocumentsSuccess,
  getDocumentsFailure,
  updateJewellerDocumentRequest,
} from "./jewellerDocumentSlice";

function* getDocumentConfigurationSaga() {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/getDocumentConfiguration`
      )
    );
    const files = response?.data?.data?.file;
    yield put(
      getDocumentConfigurationSuccess(Array.isArray(files) ? files : [])
    );
  } catch (error) {
    yield put(getDocumentConfigurationFailure());
    if (error.response?.status !== 404) {
      const message =
        error.response?.data?.message || "Failed to load document configuration";
      toast.error(message);
    }
  }
}

const mimeFromExtension = (extension) => {
  const mimeByExtension = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    pdf: "application/pdf",
    webp: "image/webp",
    gif: "image/gif",
  };
  return mimeByExtension[extension] || "";
};

const prepareUploadFile = (file) => {
  const rawName = String(file?.name || "document");
  const dotIndex = rawName.lastIndexOf(".");
  const rawBase = dotIndex > 0 ? rawName.slice(0, dotIndex) : rawName;
  const extension = dotIndex > 0 ? rawName.slice(dotIndex + 1).toLowerCase() : "";
  const safeBase = rawBase.replace(/[^a-zA-Z0-9_-]+/g, "_") || "document";
  const safeName = extension ? `${safeBase}.${extension}` : safeBase;
  const type = mimeFromExtension(extension) || file?.type || "application/octet-stream";
  return new File([file], safeName, {
    type,
    lastModified: file?.lastModified || Date.now(),
  });
};

function* uploadJewellerDocumentSaga({ payload }) {
  try {
    const formData = new FormData();
    const fileMeta = [];
    const uploadFiles = [];
    (payload.documents || []).forEach((documentItem) => {
      (documentItem.files || []).forEach((file) => {
        fileMeta.push({
          fileName: documentItem.fileName,
          filePath: "",
        });
        uploadFiles.push(prepareUploadFile(file));
      });
    });
    formData.append("jewellerId", payload.jewellerId);
    formData.append("file", JSON.stringify(fileMeta));
    uploadFiles.forEach((file) => {
      formData.append("files", file, file.name);
    });

    const token = window.localStorage.getItem("adminAccessToken") || "";
    const response = yield call(() =>
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/uploadDocument`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            jeweller: payload.jewellerId,
          },
          body: formData,
        }
      )
    );
    const data = yield call(() => response.json().catch(() => ({})));

    if (!response.ok || data?.success === false) {
      const message = data?.message || "File upload failed";
      yield put(
        uploadJewellerDocumentFailure({
          cardIndex: payload.cardIndex,
          message,
        })
      );
      toast.error(message);
      return;
    }

    toast.success(data?.message || "Document uploaded successfully");
    yield put(
      uploadJewellerDocumentSuccess({
        cardIndex: payload.cardIndex,
        fileName: payload.fileName,
        data: data?.data || null,
      })
    );
  } catch (error) {
    const message = error?.message || "Failed to upload document";
    yield put(
      uploadJewellerDocumentFailure({
        cardIndex: payload.cardIndex,
        message,
      })
    );
    toast.error(message);
  }
}

function* saveDocumentConfigurationSaga({ payload }) {
  try {
    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/saveDocumentConfiguration`,
        { file: payload?.file || [] }
      )
    );

    if (response?.data?.success === false) {
      const message =
        response?.data?.message || "Failed to save document configuration";
      yield put(saveDocumentConfigurationFailure());
      toast.error(message);
      return;
    }

    const files = response?.data?.data?.file;
    yield put(
      saveDocumentConfigurationSuccess(Array.isArray(files) ? files : [])
    );
    toast.success(
      response?.data?.message || "Document configuration saved successfully"
    );
  } catch (error) {
    const message =
      error.response?.data?.message || "Failed to save document configuration";
    yield put(saveDocumentConfigurationFailure());
    toast.error(message);
  }
}

function* updateJewellerDocumentSaga({ payload }) {
  try {
    const formData = new FormData();
    const fileMeta = [];
    const uploadFiles = [];
    (payload.documents || []).forEach((documentItem) => {
      (documentItem.files || []).forEach((file) => {
        fileMeta.push({ fileName: documentItem.fileName, filePath: "" });
        uploadFiles.push(prepareUploadFile(file));
      });
    });
    formData.append("jewellerId", payload.jewellerId);
    formData.append("file", JSON.stringify(fileMeta));
    uploadFiles.forEach((file) => formData.append("files", file, file.name));

    const token = window.localStorage.getItem("adminAccessToken") || "";
    const response = yield call(() =>
      fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/updateDocument`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            jeweller: payload.jewellerId,
          },
          body: formData,
        }
      )
    );
    const data = yield call(() => response.json().catch(() => ({})));
    if (!response.ok || data?.success === false) {
      const message = data?.message || "Document update failed";
      yield put(uploadJewellerDocumentFailure({ cardIndex: payload.cardIndex, message }));
      toast.error(message);
      return;
    }
    toast.success(data?.message || "Document updated successfully");
    yield put(
      uploadJewellerDocumentSuccess({
        cardIndex: payload.cardIndex,
        data: data?.data || null,
      })
    );
  } catch (error) {
    const message = error?.message || "Failed to update document";
    yield put(uploadJewellerDocumentFailure({ cardIndex: payload.cardIndex, message }));
    toast.error(message);
  }
}

function* getDocumentsSaga({ payload }) {
  const jewellerId = payload?.jewellerId || "";
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller-document/getDocuments`,
        { params: { jewellerId } }
      )
    );

    if (response?.data?.success === false) {
      const message = response?.data?.message || "Failed to fetch documents";
      yield put(getDocumentsFailure({ jewellerId }));
      toast.error(message);
      return;
    }

    const data = response?.data?.data;
    const records = Array.isArray(data) ? data : data ? [data] : [];
    yield put(getDocumentsSuccess({ jewellerId, records }));
  } catch (error) {
    if (error.response?.status === 404) {
      yield put(getDocumentsFailure({ jewellerId }));
      return;
    }
    const message =
      error.response?.data?.message || "Failed to fetch documents";
    yield put(getDocumentsFailure({ jewellerId }));
    toast.error(message);
  }
}

export function* jewellerDocumentSaga() {
  yield takeLatest(
    getDocumentConfigurationRequest.type,
    getDocumentConfigurationSaga
  );
  yield takeLatest(
    uploadJewellerDocumentRequest.type,
    uploadJewellerDocumentSaga
  );
  yield takeLatest(
    saveDocumentConfigurationRequest.type,
    saveDocumentConfigurationSaga
  );
  yield takeLatest(getDocumentsRequest.type, getDocumentsSaga);
  yield takeLatest(updateJewellerDocumentRequest.type, updateJewellerDocumentSaga);
}
